import React, { useState, useEffect, useRef, Fragment } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    CardProps,
    Divider,
    TextField,
} from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { useTheme } from '@material-ui/core/styles';
import useLead from 'src/hooks/useLead';
import { Phone as CallIcon } from 'react-feather';
import { PhoneMissed as PhoneMissedIcon } from 'react-feather';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { handleGetPhoneAgencies } from 'src/modules/store/helpers';
import SITE_SETTINGS from 'src/constants/whiteLabel';
import useAuth from 'src/hooks/useAuth';
import { TAreas } from 'src/constants/lead/leadTypes';
import { removeTwilioFromMessage } from '../utils';
import { handleGetArea } from 'src/modules/calls/helpers/handleGetArea';
import { formatPhoneByCountryCode } from 'src/modules/phones/utils/formatPhoneByCountryCode';
import { makeCall, MakeCallHandle } from 'src/shared/communications';
import { getCountryCode } from 'src/utils/format/getCountryCode';
import { USER_LEVEL_ACCESS } from 'src/components/access';

const types = ['first_call', 'follow_call'];
const HIDDEN_TAB_HANGUP_MS = 2 * 60 * 1000;

/**
 * Represent the component to make a call to a lead
 *
 * @param {*} { ...props }
 * @return {*}
 */
export const MakeCall = ({ ...props }: CardProps) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const { user } = useAuth();
    const { callToken, generateToken, lead, addComment } = useLead();
    const { enqueueSnackbar } = useSnackbar();
    const [callStatus, setCallStatus] = useState('Calls.RegisteringDevice');
    const [typeCall, setTypeCall] = useState('first_call');
    const [currentArea, setCurrentArea] = useState<TAreas>('nuevos');
    const callHandleRef = useRef<MakeCallHandle | null>(null);
    const commentAddedRef = useRef(false);
    const [isInCall, setIsInCall] = useState(false);
    const [currentCallerId, setCurrentCallerId] = useState('');

    const formatPhones = (phone: string) => {
        if (!phone) return '';

        let normalizedPhone = phone.replace(/\+521/g, '+52');

        return formatPhoneByCountryCode(
            normalizedPhone,
            getCountryCode(normalizedPhone),
            getCountryCode(lead.store.countryCode)
        );
    };

    useEffect(() => {
        if (lead.type) {
            const area = handleGetArea({
                type: lead.type,
                carType: lead.carType,
            });
            setCurrentArea(area);

            const { success, phone, callerId } = handleGetPhoneAgencies({
                area: currentArea,
                areas: lead.store.areas || [],
            });
            if (success) {
                const currentPhone = callerId || phone;
                const formattedPhone = formatPhones(currentPhone || '');

                if (user.isVerifiedCallerId)
                    setCurrentCallerId(formatPhones(user?.phone || ''));
                else if (lead.agent?.isVerifiedCallerId)
                    setCurrentCallerId(formatPhones(lead.agent?.phone || ''));
                else setCurrentCallerId(formattedPhone);
            }
        }
    }, [lead]);

    const disconnectActiveCall = () => {
        if (!callHandleRef.current) return;

        callHandleRef.current.disconnect();
        callHandleRef.current = null;
        setIsInCall(false);
    };

    useEffect(() => {
        if (callToken) {
            setCallStatus('Calls.RegisteredDevice');
            return;
        }

        disconnectActiveCall();
    }, [callToken]);

    useEffect(() => {
        let hiddenHangUpTimer: number | undefined;

        const hangUpFromInactivity = () => {
            if (!callHandleRef.current) return;

            callHandleRef.current.disconnect();
            callHandleRef.current = null;
            setIsInCall(false);
            setCallStatus('Calls.RegisteredDevice');
        };

        const clearHiddenHangUpTimer = () => {
            if (hiddenHangUpTimer !== undefined) {
                window.clearTimeout(hiddenHangUpTimer);
                hiddenHangUpTimer = undefined;
            }
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                clearHiddenHangUpTimer();
                hiddenHangUpTimer = window.setTimeout(
                    hangUpFromInactivity,
                    HIDDEN_TAB_HANGUP_MS
                );
                return;
            }

            clearHiddenHangUpTimer();
        };

        const handlePageLifecycleEnd = () => {
            clearHiddenHangUpTimer();
            hangUpFromInactivity();
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('pagehide', handlePageLifecycleEnd);
        window.addEventListener('beforeunload', handlePageLifecycleEnd);

        return () => {
            clearHiddenHangUpTimer();
            document.removeEventListener(
                'visibilitychange',
                handleVisibilityChange
            );
            window.removeEventListener('pagehide', handlePageLifecycleEnd);
            window.removeEventListener('beforeunload', handlePageLifecycleEnd);
            callHandleRef.current?.disconnect();
            callHandleRef.current = null;
        };
    }, []);

    const callCustomer = async () => {
        if (!lead.agent) {
            enqueueSnackbar(t('SnackBar.NoAgent'), {
                variant: 'error',
            });
            return;
        }
        if (!callToken) return;

        const formattedCallerId = formatPhones(currentCallerId);
        const toNumber = formatPhoneByCountryCode(
            lead.phone,
            lead.countryCode,
            lead.store.countryCode
        );

        const params: Record<string, string | null> = {
            user: user._id,
            from: formattedCallerId,
            callerId: formattedCallerId,
            area: currentArea,
            To: toNumber,
            lead: lead._id,
            typeCall,
            store: lead.store._id,
            client: SITE_SETTINGS.client,
            record:
                lead.store.isRecording === true
                    ? 'record-from-answer'
                    : 'do-not-record',
            assignedBy:
                user.accessLevel !== USER_LEVEL_ACCESS ? user._id : null,
            type: 'dpx',
        };

        setCallStatus('Calls.Ringing');
        commentAddedRef.current = false;

        try {
            const handle = await makeCall({
                token: callToken,
                to: toNumber,
                from: formattedCallerId,
                params,
                onRegistering: () => setCallStatus('Calls.RegisteringDevice'),
                onTokenWillExpire: () => generateToken(lead.store._id),
                onAccept: () => {
                    setIsInCall(true);
                    setCallStatus('Calls.InCall');
                },
                onDisconnect: () => {
                    setIsInCall(false);
                    setCallStatus('Calls.RegisteredDevice');
                    callHandleRef.current = null;
                    if (!commentAddedRef.current) {
                        commentAddedRef.current = true;
                        addComment({
                            pending: true,
                            action: ['call'],
                            reschedule: moment().add(5, 'minutes').format(),
                            comment: `Se ha realizado una llamada con ${lead.name}`,
                            user: lead?.agent || user,
                            createdAt: Date.now(),
                            ...(user?.accessLevel !== USER_LEVEL_ACCESS && {
                                assignedBy: user,
                            }),
                        });
                    }
                },
                onError: (message) =>
                    setCallStatus('Error: ' + removeTwilioFromMessage(message)),
            });

            callHandleRef.current = handle;
        } catch (error) {
            if (error instanceof Error)
                setCallStatus(
                    'Error: ' + removeTwilioFromMessage(error.message)
                );
        }
    };

    const hangUp = () => {
        try {
            setCallStatus('Calls.RegisteredDevice');
            disconnectActiveCall();
            if (!commentAddedRef.current) {
                commentAddedRef.current = true;
                const newComment: Record<string, unknown> = {
                    pending: true,
                    action: ['call'],
                    reschedule: moment().add(5, 'minutes').format(),
                    comment: `Se ha realizado una llamada con ${lead.name}`,
                    user: lead?.agent || user,
                    createdAt: Date.now(),
                };
                if (user?.accessLevel !== USER_LEVEL_ACCESS)
                    newComment.assignedBy = user;
                addComment(newComment);
            }
        } catch (err) {
            if (err instanceof Error)
                setCallStatus('Error: ' + removeTwilioFromMessage(err.message));
        }
    };

    const getCallerIdOptions = () => {
        const { success, phone, callerId } = handleGetPhoneAgencies({
            area: currentArea,
            areas: lead.store.areas || [],
        });

        if (!success) return null;

        const agent = lead.agent;

        let agentFormattedPhone = formatPhones(agent?.phone || '');

        let currentUserFormattedPhone = formatPhones(user?.phone || '');

        let storeFormattedPhone = formatPhones(phone || '');

        let callerIdFormatted = formatPhones(callerId || '');

        return (
            <Fragment>
                <option value={callerIdFormatted || storeFormattedPhone}>
                    {t('Calls.CallStore')}
                </option>
                {user?.isVerifiedCallerId && (
                    <option value={currentUserFormattedPhone}>
                        {t('Calls.CallUser')}
                    </option>
                )}
                {lead.agent?.isVerifiedCallerId && (
                    <option value={agentFormattedPhone}>
                        {t('Calls.CallAgent')}
                    </option>
                )}
            </Fragment>
        );
    };

    if (!callToken) return null;

    return (
        <Card {...props} data-testid="make-call-component">
            <CardHeader
                data-testid="call-status-header"
                title={`${t('Leads.CallStatus')}: ${t(callStatus)}`}
            />
            <Divider />
            <CardContent>
                <Box mt={2}>
                    <TextField
                        fullWidth
                        style={{ marginBottom: '1em' }}
                        name="type"
                        onChange={(e) => setTypeCall(e.target.value)}
                        select
                        variant="outlined"
                        SelectProps={{ native: true }}
                    >
                        {types.map((item) => (
                            <option key={item} value={item}>
                                {t(`RecordingTests.${item}`)}
                            </option>
                        ))}
                    </TextField>
                    {(lead.agent?.isVerifiedCallerId ||
                        user?.isVerifiedCallerId) && (
                        <TextField
                            fullWidth
                            style={{ marginBottom: '1em' }}
                            onChange={(e) => setCurrentCallerId(e.target.value)}
                            select
                            variant="outlined"
                            SelectProps={{ native: true }}
                            value={currentCallerId}
                        >
                            {getCallerIdOptions()}
                        </TextField>
                    )}
                    <Button
                        disabled={
                            callStatus !== 'Calls.RegisteredDevice' || isInCall
                        }
                        variant="contained"
                        type="submit"
                        size="large"
                        fullWidth
                        color="primary"
                        startIcon={<CallIcon />}
                        onClick={callCustomer}
                        style={{
                            backgroundColor: theme.palette.success.main,
                            height: 60,
                        }}
                    >
                        {t('Leads.MakeCall')}
                    </Button>
                    <Button
                        disabled={!isInCall}
                        variant="contained"
                        type="submit"
                        size="large"
                        fullWidth
                        color="primary"
                        startIcon={<PhoneMissedIcon />}
                        onClick={() => hangUp()}
                        style={{
                            marginTop: '1em',
                            backgroundColor: theme.palette.error.main,
                            height: 60,
                        }}
                    >
                        {t('Leads.HangUp')}
                    </Button>
                </Box>
            </CardContent>
        </Card>
    );
};
